class CartNotification extends HTMLElement {
  constructor() {
    super();

    this.notification = document.getElementById('cart-notification');
    this.header = document.querySelector('sticky-header');
    this.onBodyClick = this.handleBodyClick.bind(this);
    this.onDocumentSubmit = this.handleLegacyCartAddSubmit.bind(this);
    this.scrim = this.querySelector('.cart-notification__scrim');

    this.notification.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelectorAll('button[type="button"]').forEach((closeButton) =>
      closeButton.addEventListener('click', this.close.bind(this))
    );
    if (this.scrim) this.scrim.addEventListener('click', this.close.bind(this));
  }

  connectedCallback() {
    this.updateStickyCartCta();
    document.addEventListener('submit', this.onDocumentSubmit, true);
    this.prefetchAddons();
  }

  // The "Popular add-ons" grid lives in sections/pt-cart-notification-addons and
  // is fetched once, after window load, so it is not part of every page's HTML.
  // renderContents() waits for it (briefly) so the first open is complete.
  prefetchAddons() {
    const idle = () => ('requestIdleCallback' in window ? requestIdleCallback(() => this.ensureAddons(), { timeout: 3000 }) : setTimeout(() => this.ensureAddons(), 1000));
    if (document.readyState === 'complete') idle();
    else window.addEventListener('load', idle, { once: true });
  }

  ensureAddons() {
    const container = this.querySelector('[data-cart-notification-addons]');
    if (!container || !container.dataset.addonsSrc) return Promise.resolve();
    if (!this.addonsPromise) {
      this.addonsPromise = fetch(container.dataset.addonsSrc, { headers: { Accept: 'text/html' } })
        .then((response) => (response.ok ? response.text() : Promise.reject(new Error(`add-ons ${response.status}`))))
        .then((html) => {
          const section = new DOMParser().parseFromString(html, 'text/html').querySelector('.shopify-section');
          if (section) container.innerHTML = section.innerHTML;
          delete container.dataset.addonsSrc;
        })
        .catch((error) => {
          console.warn('Cart pop-up add-ons not loaded', error);
          this.addonsPromise = null;
        });
    }
    return this.addonsPromise;
  }

  disconnectedCallback() {
    document.removeEventListener('submit', this.onDocumentSubmit, true);
  }

  open() {
    this.classList.add('active');
    this.notification.classList.add('animate', 'active');

    this.notification.addEventListener(
      'transitionend',
      () => {
        this.notification.focus({ preventScroll: true });
      },
      { once: true }
    );

    document.body.addEventListener('click', this.onBodyClick);
  }

  close() {
    this.classList.remove('active');
    this.notification.classList.remove('active');
    document.body.removeEventListener('click', this.onBodyClick);

    removeTrapFocus(this.activeElement);
  }

  renderContents(parsedState) {
    // Give an in-flight add-ons fetch up to 800 ms, then open regardless.
    const addons = Promise.race([this.ensureAddons(), new Promise((resolve) => setTimeout(resolve, 800))]);
    addons.then(() => this.renderContentsNow(parsedState));
  }

  renderContentsNow(parsedState) {
    this.cartItemKey = parsedState.key;
    this.getSectionsToRender().forEach((section) => {
      document.getElementById(section.id).innerHTML = this.getSectionInnerHTML(
        parsedState.sections[section.id],
        section.selector
      );
    });

    this.updateEnhancedState(parsedState);
    this.updateAddonStates(parsedState);
    this.updateStickyCartCta();
    // assets/pt-deck-cta.js relabels deck buttons from the new cart.
    document.dispatchEvent(new CustomEvent('pt:cart-changed'));
    this.open();
    this.submitContext = null;
  }

  // One branded layout for every add (it used to be deck-only, so extras added on
  // the Gifts page got the plain theme pop-up). The .is-deck-added class still
  // drives that layout's CSS; the copy changes with what was added and whether
  // the bag already has a deck. Without one, the pop-up offers the deck.
  updateEnhancedState(parsedState) {
    const deckHandle = 'pickleball-twist';
    const items = parsedState?.items || [];
    const addedProduct = document.getElementById('cart-notification-product')?.querySelector('[data-cart-notification-deck]');
    const addedDeck = addedProduct?.dataset.cartNotificationDeck === 'true';
    // parsedState is the added line, not the cart; the rendered product marker carries the cart's deck status.
    const marker = document.getElementById('cart-notification-product')?.querySelector('[data-cart-has-deck]');
    const hasDeck = addedDeck || marker?.dataset.cartHasDeck === 'true' || items.some((item) => item.handle === deckHandle || item.product_handle === deckHandle);
    const headingText = this.querySelector('[data-cart-notification-heading-text]');
    const eyebrow = this.querySelector('[data-cart-notification-eyebrow]');
    const summary = this.querySelector('[data-cart-notification-summary]');

    this.classList.add('is-deck-added');
    this.classList.toggle('needs-deck', !hasDeck);
    this.classList.toggle('can-pack', Number(marker?.dataset.cartDeckCount || 0) === 1);

    const heading = addedDeck ? 'Deck added to your bag' : 'Added to your bag';
    this.notification.setAttribute('aria-label', heading);
    if (headingText) headingText.textContent = heading;

    let copy = ['Nice, the main event is in your bag', 'Checkout now, or add a court-ready extra.'];
    if (!hasDeck) copy = ["Don't forget the deck", 'Pickleball Twist® is the main event, and it ships free.'];
    else if (!addedDeck) copy = ['Nice pick', 'Checkout now, or add another extra.'];
    if (eyebrow) eyebrow.textContent = copy[0];
    if (summary) summary.textContent = copy[1];
  }

  updateAddonStates(parsedState) {
    const items = parsedState?.items || [];
    const addedVariantId = Number(parsedState?.variant_id || 0);
    this.querySelectorAll('[data-cart-notification-addon]').forEach((button) => {
      const variantId = Number(button.dataset.variantId);
      // A disabled button stays "Added" (sticky after an add), but not when it
      // is disabled because the picked variant is sold out.
      const soldOut = button.dataset.available === 'false';
      const inCart =
        (button.disabled && !soldOut) ||
        variantId === addedVariantId ||
        items.some((item) => Number(item.variant_id) === variantId);
      const input = button.closest('form')?.querySelector('[name="id"]');
      const label = button.querySelector('.cart-notification__addon-action');

      button.disabled = inCart || soldOut;
      if (inCart || soldOut) {
        button.setAttribute('aria-disabled', 'true');
      } else {
        button.removeAttribute('aria-disabled');
      }
      if (input) input.disabled = inCart || soldOut;
      if (label) {
        label.textContent = inCart
          ? button.dataset.addedLabel || 'Added'
          : soldOut
            ? 'Sold out'
            : button.dataset.addLabel || 'Add';
      }
    });
  }

  updateStickyCartCta() {
    const stickyCartCta = document.getElementById('pt-sticky-cart-cta');
    if (!stickyCartCta) return;

    const cartCount = document.querySelector('#cart-icon-bubble .cart-count-bubble span[aria-hidden="true"]');
    const count = Number(cartCount?.textContent?.trim() || 0);
    const countLabel = stickyCartCta.querySelector('[data-pt-sticky-cart-count]');

    stickyCartCta.hidden = count < 1;
    stickyCartCta.classList.toggle('is-visible', count > 0);
    // The empty-cart buy bar (snippets/cart-notification) gives way once something is in the cart.
    document.getElementById('pt-sticky-buy')?.classList.toggle('is-suppressed', count > 0);

    if (countLabel) {
      countLabel.textContent = count === 1 ? '1 item' : `${count} items`;
    }
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-notification-product',
        selector: `[id="cart-notification-product-${this.cartItemKey}"]`,
      },
      {
        id: 'cart-notification-button',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  handleBodyClick(evt) {
    const target = evt.target;
    if (target !== this.notification && !target.closest('cart-notification')) {
      const disclosure = target.closest('details-disclosure, header-menu');
      this.activeElement = disclosure ? disclosure.querySelector('summary') : null;
      this.close();
    }
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  setSubmitContext(element) {
    this.submitContext = element;
  }

  handleLegacyCartAddSubmit(event) {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (form.closest('product-form')) return;
    if (form.dataset.ptNativeCartAdd === 'true') return;

    const action = form.getAttribute('action') || '';
    const actionPath = action ? new URL(action, window.location.origin).pathname : '';
    if (!actionPath.endsWith('/cart/add')) return;
    if (!form.querySelector('[name="id"]')) return;

    event.preventDefault();
    this.submitLegacyCartAdd(form);
  }

  submitLegacyCartAdd(form) {
    const submitButton = form.querySelector('[type="submit"]');

    if (submitButton?.getAttribute('aria-disabled') === 'true' || submitButton?.disabled) return;

    if (submitButton) {
      submitButton.setAttribute('aria-disabled', 'true');
      submitButton.classList.add('loading');
    }

    const config = fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];

    const formData = new FormData(form);
    formData.append(
      'sections',
      this.getSectionsToRender().map((section) => section.id)
    );
    formData.append('sections_url', window.location.pathname);
    this.setActiveElement(document.activeElement);
    this.setSubmitContext(form);
    config.body = formData;

    fetch(`${routes.cart_add_url}`, config)
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          throw new Error(response.description || response.message || 'Unable to add item to cart.');
        }

        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'legacy-cart-add-form',
          productVariantId: formData.get('id'),
          cartData: response,
        });
        this.renderContents(response);
      })
      .catch((error) => {
        console.error(error);
        window.location = window.routes.cart_url;
      })
      .finally(() => {
        if (submitButton) {
          submitButton.classList.remove('loading');
          submitButton.removeAttribute('aria-disabled');
        }
      });
  }
}

customElements.define('cart-notification', CartNotification);
